import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Signs a seller out when the portal has been left alone.
 *
 * The till machine in a shop is rarely one person's: it sits on a counter,
 * sometimes a shared back-office PC, and a seller who walks away leaves their
 * orders, customers, balances and payout numbers on screen for whoever sits
 * down next. The session itself lasts for weeks, so nothing closed it.
 *
 * Activity in any tab counts for all of them, so a seller working in one
 * window is not signed out of another. Time spent with the laptop shut counts
 * as idle: the clock is read when the tab wakes, not ticked while it sleeps.
 */

/** Long enough to read a report or serve a customer; short enough to matter. */
export const IDLE_LIMIT_MS = 20 * 60 * 1000;

/** The seller is asked before they are signed out, never after. */
export const WARN_BEFORE_MS = 60 * 1000;

/** Shared so a second tab knows the first one is being used. */
const LAST_ACTIVITY_KEY = 'beetle.lastActivity';

/** Mouse moves fire constantly; the stored time only needs to be roughly right. */
const WRITE_EVERY_MS = 5000;

const ACTIVITY_EVENTS = [
  'mousedown', 'keydown', 'touchstart', 'scroll', 'wheel', 'mousemove',
];

function readLastActivity() {
  try {
    const stored = Number(window.localStorage.getItem(LAST_ACTIVITY_KEY));
    return Number.isFinite(stored) && stored > 0 ? stored : Date.now();
  } catch {
    // Private browsing, or storage turned off: this tab keeps its own clock.
    return Date.now();
  }
}

function writeLastActivity(at) {
  try {
    window.localStorage.setItem(LAST_ACTIVITY_KEY, String(at));
  } catch {
    /* not fatal — see above */
  }
}

/**
 * @param {object} options
 * @param {() => void} options.onIdle       called once the limit has passed
 * @param {boolean}    options.enabled      false while nobody is signed in
 * @param {number}     options.idleMs       how long counts as left alone
 * @param {number}     options.warnMs       how long the warning is shown for
 * @returns {{ warning: boolean, secondsLeft: number, staySignedIn: () => void }}
 */
export default function useIdleLogout({
  onIdle,
  enabled = true,
  idleMs = IDLE_LIMIT_MS,
  warnMs = WARN_BEFORE_MS,
} = {}) {
  const [warning, setWarning] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(Math.ceil(warnMs / 1000));

  const lastActivityRef = useRef(Date.now());
  const lastWriteRef = useRef(0);
  const firedRef = useRef(false);
  const onIdleRef = useRef(onIdle);

  useEffect(() => {
    onIdleRef.current = onIdle;
  }, [onIdle]);

  const markActive = useCallback((at = Date.now()) => {
    lastActivityRef.current = at;
    firedRef.current = false;
    setWarning(false);
  }, []);

  /** "I am still here" — from the warning's own button. */
  const staySignedIn = useCallback(() => {
    const now = Date.now();
    markActive(now);
    writeLastActivity(now);
    lastWriteRef.current = now;
  }, [markActive]);

  useEffect(() => {
    if (!enabled) return undefined;

    // Pick up where this browser left off, so closing and reopening a tab is
    // not a fresh twenty minutes.
    const resumed = readLastActivity();
    lastActivityRef.current = resumed;
    lastWriteRef.current = resumed;

    const onActivity = () => {
      const now = Date.now();
      markActive(now);
      if (now - lastWriteRef.current >= WRITE_EVERY_MS) {
        writeLastActivity(now);
        lastWriteRef.current = now;
      }
    };

    // Another tab being used keeps this one alive.
    const onStorage = (event) => {
      if (event.key !== LAST_ACTIVITY_KEY || !event.newValue) return;
      const at = Number(event.newValue);
      if (Number.isFinite(at) && at > lastActivityRef.current) markActive(at);
    };

    ACTIVITY_EVENTS.forEach(name =>
      window.addEventListener(name, onActivity, { passive: true }));
    window.addEventListener('storage', onStorage);

    const tick = () => {
      const idleFor = Date.now() - lastActivityRef.current;

      if (idleFor >= idleMs) {
        if (firedRef.current) return;
        firedRef.current = true;
        setWarning(false);
        onIdleRef.current?.();
        return;
      }

      const untilOut = idleMs - idleFor;
      if (untilOut <= warnMs) {
        setWarning(true);
        setSecondsLeft(Math.max(0, Math.ceil(untilOut / 1000)));
      } else if (warning) {
        setWarning(false);
      }
    };

    // Once a second: fine for a countdown, and nothing heavier is needed since
    // the elapsed time is read from the clock rather than counted up.
    const timer = setInterval(tick, 1000);
    tick();

    return () => {
      clearInterval(timer);
      window.removeEventListener('storage', onStorage);
      ACTIVITY_EVENTS.forEach(name =>
        window.removeEventListener(name, onActivity));
    };
    // `warning` is read inside tick only to clear it; including it would rebuild
    // the listeners every second.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, idleMs, warnMs, markActive]);

  return { warning, secondsLeft, staySignedIn };
}
